// RouletteSpinner.jsx
import React, { useState, useEffect } from 'react';
import { useUser } from '../context/userContext';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/firestore';
import { IoCheckmarkCircle } from "react-icons/io5";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { MdOutlineTimer } from "react-icons/md";
import toast from 'react-hot-toast';

const RouletteSpinner = () => {
  const { user, balance, setBalance } = useUser();
  const values = [100, 200, 300, 400, 500, 600, 700, 800, 900, 1000];
  const [selectedIndex, setSelectedIndex] = useState(2);
  const [isSpinning, setIsSpinning] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [lastSpinTime, setLastSpinTime] = useState(null);
  const [rotationDegrees, setRotationDegrees] = useState(0);
  const [spinSpeed, setSpinSpeed] = useState(0);
  const [availableSpins, setAvailableSpins] = useState(5);
  const [totalSpins, setTotalSpins] = useState(0);

  // Check Firebase for initial state
  useEffect(() => {
    const checkLastSpin = async () => {
      if (!user?.uid) return;

      try {
        const userRef = doc(db, "telegramUsers", user.uid.toString());
        const userDoc = await getDoc(userRef);
        const data = userDoc.data();
        
        // Get stored time from localStorage as backup
        const storedTime = localStorage.getItem(`lastSpinTime_${user.uid}`);
        const storedTimeDate = storedTime ? new Date(parseInt(storedTime)) : null;
        
        let lastSpinDate;
        if (data?.lastSpinTime) {
          lastSpinDate = data.lastSpinTime.toDate();
        } else if (storedTimeDate) {
          lastSpinDate = storedTimeDate;
        }

        if (lastSpinDate) {
          setLastSpinTime(lastSpinDate);
          const now = new Date();
          const timeDiff = now - lastSpinDate;
          const timeLeft = Math.max(7200000 - timeDiff, 0);
          setCooldown(Math.ceil(timeLeft / 1000));
          setAvailableSpins(timeLeft > 0 ? 0 : 5);
          setTotalSpins(data?.totalSpins || 0);
        } else {
          setAvailableSpins(5);
          if (!data?.totalSpins) {
            await updateDoc(userRef, { 
              totalSpins: 0,
              availableSpins: 5
            });
          }
        }
      } catch (error) {
        console.error("Error checking last spin:", error);
        toast.error("Failed to load spin data");
      }
    };
    checkLastSpin();
  }, [user]);

  // Cooldown timer
  useEffect(() => {
    let timer;
    if (cooldown > 0) {
      timer = setInterval(() => {
        setCooldown(prev => {
          const newCooldown = Math.max(prev - 1, 0);
          if (newCooldown === 0) {
            setAvailableSpins(5);
            // Update Firebase when cooldown ends
            if (user?.uid) {
              const userRef = doc(db, "telegramUsers", user.uid.toString());
              updateDoc(userRef, { availableSpins: 5 })
                .catch(error => console.error("Error updating spins:", error));
            }
          }
          return newCooldown;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [cooldown, user]);

  // Spinning animation and win calculation
  useEffect(() => {
    if (!isSpinning) return;

    const spinDuration = 6000;
    const startSpeed = 1800;
    const endSpeed = 360;
    let currentSpeed = startSpeed;
    let elapsed = 0;
    let lastUpdate = Date.now();
    
    // Determine win at start of spin
    const finalWinIndex = Math.floor(Math.random() * values.length);
    const winAmount = values[finalWinIndex];

    const intervalId = setInterval(() => {
      const now = Date.now();
      const delta = now - lastUpdate;
      elapsed += delta;
      lastUpdate = now;

      const progress = Math.min(1, elapsed / spinDuration);
      currentSpeed = startSpeed - (startSpeed - endSpeed) * progress;
      
      setRotationDegrees(prev => {
        const increment = (currentSpeed * delta) / 1000;
        return prev + increment;
      });

      // Update display during spin
      const currentRotation = rotationDegrees % 360;
      const currentIndex = Math.floor((currentRotation / (360 / values.length))) % values.length;
      setSelectedIndex(currentIndex);

      if (elapsed >= spinDuration) {
        clearInterval(intervalId);
        
        // Set final position and trigger win
        setRotationDegrees(prev => {
          const targetRotation = (finalWinIndex * (360 / values.length)) + (360 * Math.floor(prev / 360));
          return targetRotation;
        });
        setSelectedIndex(finalWinIndex);
        setIsSpinning(false);
        
        // Reduce available spins before handling win
        setAvailableSpins(prev => {
          const newSpins = Math.max(prev - 1, 0);
          handleWin(winAmount, newSpins);
          return newSpins;
        });
      }
    }, 16);

    return () => clearInterval(intervalId);
  }, [isSpinning, values]);

  const handleWin = async (amount, newSpins) => {
    if (!user?.uid) return;

    const userRef = doc(db, "telegramUsers", user.uid.toString());
    try {
      // Get fresh user data
      const userDoc = await getDoc(userRef);
      const currentData = userDoc.data();
      
      if (!currentData) {
        toast.error("User data not found");
        return;
      }

      const now = new Date();
      const updates = {
        balance: (currentData.balance || 0) + amount,
        lastSpinTime: now,
        availableSpins: newSpins,
        totalSpins: (currentData.totalSpins || 0) + 1,
        lastWinAmount: amount,
        totalWinnings: (currentData.totalWinnings || 0) + amount,
        taskPoints: (currentData.taskPoints || 0) + amount // Update task points as well
      };

      // Atomic update
      await updateDoc(userRef, updates);

      // Update local storage
      localStorage.setItem(`lastSpinTime_${user.uid}`, now.getTime().toString());
      localStorage.setItem(`availableSpins_${user.uid}`, newSpins.toString());

      // Update local state
      setBalance(updates.balance);
      setTotalSpins(updates.totalSpins);
      setCooldown(7200);

      toast.success(`Won ${amount} $TFlash!`);
    } catch (error) {
      console.error("Error updating after win:", error);
      toast.error("Failed to update balance");
      // Restore available spins on error
      setAvailableSpins(prev => prev + 1);
    }
  };

  const spin = () => {
    if (!user?.uid) {
      toast.error("Please connect your account");
      return;
    }

    if (!isSpinning && cooldown === 0 && availableSpins > 0) {
      setIsSpinning(true);
      // Ensure at least 5 full rotations plus random
      setRotationDegrees(prev => prev + (360 * 5) + Math.random() * 360);
    }
  };

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs}h ${mins}m ${secs}s`;
  };

  const formatNumber = (num) => {
    if (num < 100000) {
      return new Intl.NumberFormat().format(num).replace(/,/g, " ");
    } else if (num < 1000000) {
      return new Intl.NumberFormat().format(num).replace(/,/g, " ");
    } else {
      return (num / 1000000).toFixed(3).replace(".", ".") + " M";
    }
  };

  return (
    <div className='w-full flex flex-col space-y-4'>
      <div className='w-full bg-gradient-to-br from-[#1f2023] to-[#252629] rounded-[16px] p-6 
          border border-[#2c2d30] shadow-xl'>
        <div className="flex flex-col items-center space-y-4">
          {/* Main wheel container */}
          <div className='w-[240px] h-[240px] rounded-full relative'>
            {/* Background ring */}
            <div className='absolute inset-0 bg-gradient-to-br from-[#2a2b2e] to-[#323438] rounded-full 
                          border-8 border-[#1a1b1d] shadow-[inset_0_0_20px_rgba(0,0,0,0.6)]'></div>
            
            {/* Outer notches ring */}
            <div className='absolute inset-[-8px] rounded-full'>
              {[...Array(36)].map((_, i) => (
                <div
                  key={i}
                  className='absolute left-1/2 top-0 -translate-x-1/2 origin-bottom'
                  style={{
                    transform: `rotate(${i * 10}deg)`,
                    transformOrigin: '50% 128px',
                  }}
                >
                  <div className='w-[4px] h-[12px] rounded-full bg-gradient-to-b from-[#ffd700] to-[#f3b919]
                                shadow-[0_2px_4px_rgba(0,0,0,0.2)]'></div>
                </div>
              ))}
            </div>
            
            {/* Inner spinning wheel */}
            <div className='absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2
                          w-[200px] h-[200px] bg-gradient-to-br from-[#1a1b1d] to-[#2a2b2e] 
                          rounded-full border-4 border-[#616161] shadow-2xl overflow-hidden'
                 style={{ 
                   transform: `translate(-50%, -50%) rotate(${rotationDegrees}deg)`,
                   transition: isSpinning ? 'none' : 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
                 }}>
              
              {/* Value display */}
              <div className='absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2
                            w-[140px] h-[140px] rounded-full bg-[#1f2023] border-2 border-[#2c2d30]
                            flex items-center justify-center shadow-[inset_0_4px_8px_rgba(0,0,0,0.2)]'
                   style={{ transform: `translate(-50%, -50%) rotate(${-rotationDegrees}deg)` }}>
                <div className='text-center'>
                  <div className='flex items-center justify-center space-x-2'>
                    <img src='/trigocoin.png' alt='tflash' className='w-[14px]'/>
                    <span className='text-[32px] font-bold bg-gradient-to-r from-[#ffd700] to-[#f3b919] 
                                   text-transparent bg-clip-text'>
                      {values[selectedIndex]}
                    </span>
                  </div>
                  <span className='text-[14px] text-secondary'>$TFlash</span>
                </div>
              </div>
            </div>

            {/* Pointer */}
            <div className='absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-30'>
              <div className='w-6 h-8 flex flex-col items-center'>
                <div className='w-full h-6 bg-gradient-to-b from-[#ffd700] to-[#f3b919] 
                              clip-pointer shadow-lg'></div>
                <div className='w-2 h-2 rounded-full bg-[#f3b919] mt-[-1px] 
                              border-2 border-[#1a1b1d] shadow-md'></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats and Controls */}
      <div className="w-full bg-cards rounded-[12px] px-4 pt-4 pb-2 flex flex-col items-center justify-center mb-3">
        <div className="flex items-center justify-between w-full mb-2">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#2a2b2e] to-[#323438] 
                          flex items-center justify-center border border-[#616161]">
              <img src='/trigocoin.png' alt='tflash' className='w-[14px]'/>
            </div>
            <span className="text-[14px] font-medium text-secondary">Available Spins</span>
          </div>
          <span className="text-[14px] font-bold bg-gradient-to-r from-[#ffd700] to-[#f3b919] 
                          text-transparent bg-clip-text">
            {availableSpins}/5 {totalSpins > 0 && `(Total: ${totalSpins})`}
          </span>
        </div>

        <div className="w-full h-2 bg-[#2a2b2e] rounded-full overflow-hidden mb-4">
          <div className="h-full bg-gradient-to-r from-[#f3b919] to-[#ffd700] transition-all duration-500"
               style={{ width: `${(availableSpins / 5) * 100}%` }}></div>
        </div>

        <h2 className="font-medium text-secondary text-[14px]">Potential Win</h2>
        <span className="text-[26px] font-semibold flex items-center justify-center space-x-1">
          <img src='/trigocoin.png' alt='tflash' className='w-[18px]'/>
          <span>{formatNumber(values[selectedIndex])}</span>
        </span>
        {cooldown > 0 && (
          <span className="font-medium text-[13px] text-secondary flex items-center justify-center space-x-[1px]">
            <MdOutlineTimer size={14} />
            <span>{formatTime(cooldown)}</span>
          </span>
        )}
      </div>

      {/* Action Buttons */}
      <div className="w-full flex items-center justify-between space-x-2">
        <button
          onClick={spin}
          disabled={cooldown > 0 || isSpinning || availableSpins === 0}
          className={`w-[48%] px-4 py-3 flex items-center justify-center text-center rounded-[8px] font-semibold text-[14px] 
            ${cooldown > 0 || isSpinning || availableSpins === 0 ? "bg-btn2 text-[#888]" : "bg-btn"}`}
        >
          {isSpinning ? (
            <AiOutlineLoading3Quarters className="animate-spin text-[24px]" />
          ) : (
            "Spin Now"
          )}
        </button>

        <button
          onClick={() => {}}
          disabled={true}
          className="w-[48%] px-4 py-3 flex items-center justify-center text-center rounded-[8px] font-semibold text-[14px] bg-btn2 text-[#888]"
        >
          Boost Spins
        </button>
      </div>

      <style jsx>{`
        .clip-pointer {
          clip-path: polygon(50% 100%, 0 0, 100% 0);
        }
      `}</style>
    </div>
  );
};

export default RouletteSpinner;